import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderPen, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { LIVE, PersonGroup, Progress, RENAME_API, ResultView, RunsList, SummaryChips, fmtDT } from './TicketRenameParts';

// One-time Drive clean-up: preview → tick → rename → undo. Super admin only (backend enforces).
export default function TicketRenameTool({ onClose }) {
  const [jobs, setJobs] = useState(null);
  const [job, setJob] = useState(null);
  const [sel, setSel] = useState({});
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadJobs = useCallback(() => axios.get(`${RENAME_API}/jobs`).then((r) => { setJobs(r.data); return r.data; }), []);
  const openJob = useCallback((id) => axios.get(`${RENAME_API}/jobs/${id}`).then((r) => { setJob(r.data); return r.data; }), []);

  useEffect(() => {
    loadJobs().then((js) => { const live = js.find((j) => LIVE.concat('ready').includes(j.status)); if (live) openJob(live.id); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load rename runs'));
  }, [loadJobs, openJob]);

  const jobId = job?.id; const jobStatus = job?.status;
  useEffect(() => {
    if (!jobId || !LIVE.includes(jobStatus)) return undefined;
    const t = setInterval(() => openJob(jobId).then((j) => { if (!LIVE.includes(j.status)) loadJobs(); }).catch(() => {}), 1500);
    return () => clearInterval(t);
  }, [jobId, jobStatus, openJob, loadJobs]);

  const rows = job?.rows;
  useEffect(() => {
    if (jobStatus !== 'ready' || !rows) return;
    setSel(Object.fromEntries(rows.filter((r) => r.proposed && r.status !== 'same').map((r) => [r.file_id, { checked: r.status === 'ok', name: r.proposed }])));
    setFilter('all'); setQ('');
  }, [jobId, jobStatus, rows]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = [];
    (job?.rows || []).forEach((r) => {
      if (filter !== 'all' && r.status !== filter) return;
      if (needle && !`${r.current} ${r.proposed || ''} ${r.name}`.toLowerCase().includes(needle)) return;
      let g = out[out.length - 1];
      if (!g || g.folder !== r.folder) { g = { folder: r.folder, name: r.name, user_id: r.user_id, rows: [] }; out.push(g); }
      g.rows.push(r);
    });
    return out;
  }, [job, filter, q]);

  const ticked = useMemo(() => Object.entries(sel).filter(([, s]) => s.checked && s.name.trim()), [sel]);
  const toggle = (id) => setSel((p) => ({ ...p, [id]: { ...p[id], checked: !p[id].checked } }));
  const edit = (id, name) => setSel((p) => ({ ...p, [id]: { name, checked: true } }));
  const setMany = (ids, checked) => setSel((p) => { const n = { ...p }; ids.forEach((id) => { if (n[id]) n[id] = { ...n[id], checked }; }); return n; });
  const tickAllOk = () => setMany((job?.rows || []).filter((r) => r.status === 'ok').map((r) => r.file_id), true);

  const run = async (fn, err) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || err); } finally { setBusy(false); } };
  const startPlan = () => run(async () => { const r = await axios.post(`${RENAME_API}/plan`); await openJob(r.data.job_id); }, 'Could not start the scan');
  const apply = () => run(async () => {
    const items = ticked.map(([file_id, s]) => ({ file_id, new_name: s.name.trim() }));
    await axios.post(`${RENAME_API}/jobs/${job.id}/apply`, { items });
    setConfirm(false);
    setJob({ ...job, status: 'applying', applied: 0, apply_total: items.length });
  }, 'Could not start renaming');
  const undo = (id) => run(async () => { await axios.post(`${RENAME_API}/jobs/${id}/revert`); await openJob(id); toast.success('Restoring the old filenames…'); }, 'Could not undo');

  const st = job?.status;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-2 sm:p-6" data-testid="rename-modal">
      <div className="relative bg-white border border-slate-200 rounded-2xl w-full max-w-6xl h-[94vh] sm:h-[90vh] shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200">
          <div>
            <p className="text-slate-900 font-bold text-lg flex items-center gap-2"><FolderPen className="w-5 h-5 text-emerald-700" /> Standardize Drive filenames</p>
            <p className="text-xs text-slate-500">Every ticket becomes <span className="font-mono text-slate-800">Course - First Last - Exp YYYY-MM-DD</span>. Nothing changes until you hit Rename — and every run can be undone.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50" data-testid="rename-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {jobs === null && <p className="text-slate-700 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}

          {jobs !== null && !job && (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
                <p className="text-sm text-slate-800">Step 1 is a <b>read-only</b> walk of every employee's “Tickets &amp; Certificates” folder. You'll get a table grouped by person — current name → proposed name — with the sure ones ticked and anything uncertain flagged for you to decide.</p>
                <button type="button" onClick={startPlan} disabled={busy} data-testid="rename-start"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Preview changes
                </button>
              </div>
              <div><p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Previous runs</p><RunsList jobs={jobs} onOpen={openJob} onUndo={undo} busy={busy} /></div>
            </>
          )}

          {st === 'scanning' && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-3 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />
              <p className="text-slate-900 font-semibold">Reading Drive…</p>
              <p className="text-sm text-slate-700 font-mono" data-testid="rename-scan-progress">{job.progress}</p>
            </div>
          )}
          {st === 'error' && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 space-y-3">
              <p className="text-rose-800 text-sm" data-testid="rename-error">Scan failed: {job.error}</p>
              <button type="button" onClick={() => setJob(null)} className="text-sm text-slate-900 underline" data-testid="rename-back">Back</button>
            </div>
          )}

          {st === 'ready' && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <SummaryChips summary={job.summary} filter={filter} onFilter={setFilter} />
                <span className="flex-1" />
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-2" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or file" data-testid="rename-search"
                    className="bg-white border border-slate-300 rounded-lg pl-7 pr-2 py-1 text-sm text-slate-900 w-56 focus:outline-none focus:border-emerald-400" />
                </div>
                <button type="button" onClick={tickAllOk} className="text-xs text-emerald-700 hover:underline" data-testid="rename-tick-ok">Tick all OK</button>
                <button type="button" onClick={() => setMany(Object.keys(sel), false)} className="text-xs text-slate-700 hover:underline" data-testid="rename-untick-all">Untick all</button>
              </div>
              <p className="text-[11px] text-slate-500">Scanned {fmtDT(job.finished_at)} · {job.folders} folders · {job.rows.length} files. Edit any proposed name inline — editing ticks the row.</p>
              {groups.length === 0 && <p className="text-sm text-slate-500" data-testid="rename-empty">Nothing matches this filter.</p>}
              <div className="space-y-3">
                {groups.map((g) => <PersonGroup key={g.folder} group={g} sel={sel} onToggle={toggle} onEdit={edit} onGroupAll={(grp, on) => setMany(grp.rows.map((r) => r.file_id), on)} />)}
              </div>
            </>
          )}

          {st === 'applying' && <Progress value={job.applied || 0} total={job.apply_total || 0} label="Renaming in Drive…" />}
          {st === 'reverting' && <p className="text-sm text-amber-800 flex items-center gap-2" data-testid="rename-reverting"><Loader2 className="w-4 h-4 animate-spin" /> Putting the old names back…</p>}
          {(st === 'done' || st === 'reverted') && (
            <>
              {st === 'reverted' && <p className="text-sm text-emerald-800 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="rename-reverted">Undone — {job.reverted || 0} filename{job.reverted === 1 ? '' : 's'} restored{job.revert_failed ? `, ${job.revert_failed} could not be restored` : ''}.</p>}
              <ResultView job={job} onUndo={undo} undoing={busy} />
            </>
          )}
        </div>

        {(st === 'ready' || st === 'done' || st === 'reverted' || st === 'error') && (
          <div className="flex flex-wrap items-center gap-3 p-4 border-t border-slate-200">
            {st === 'ready' && (
              <button type="button" disabled={!ticked.length || busy} onClick={() => setConfirm(true)} data-testid="rename-apply"
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-50">
                Rename {ticked.length} file{ticked.length === 1 ? '' : 's'}
              </button>
            )}
            <button type="button" onClick={() => setJob(null)} className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900" data-testid="rename-rescan">
              <RefreshCw className="w-4 h-4" /> {st === 'ready' ? 'Re-scan Drive' : 'Back to start'}
            </button>
          </div>
        )}

        {confirm && (
          <div className="absolute inset-0 z-10 bg-black/60 flex items-center justify-center p-4" data-testid="rename-confirm">
            <div className="bg-white border border-slate-300 rounded-xl p-5 max-w-md w-full space-y-3 shadow-2xl">
              <p className="text-slate-900 font-bold">Rename {ticked.length} file{ticked.length === 1 ? '' : 's'} in Google Drive?</p>
              <p className="text-sm text-slate-700">Files are renamed in place (same folder, same sharing). Matching ticket records in 365Ops are updated too. You can undo this run afterwards.</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setConfirm(false)} className="px-3 py-1.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50" data-testid="rename-confirm-cancel">Cancel</button>
                <button type="button" onClick={apply} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-60" data-testid="rename-confirm-yes">
                  {busy ? 'Starting…' : 'Yes, rename'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
